import { supabase } from "@/lib/supabase";
import { ApiError } from "@/lib/api-error-handler";

// Reuse the same env var as the storefront API client.
// In dev, empty string routes through Vite's proxy; in prod, set to the real API origin.
const API_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * Authenticated fetch wrapper for the Djayma admin API.
 *
 * Attaches the current Supabase session token and the `x-admin-platform`
 * header so the backend can distinguish admin-panel requests from storefront
 * traffic.
 *
 * This is the shared foundation for ALL admin domain modules — keep it
 * minimal and generic.  Domain-specific logic belongs in its own module.
 */
export type AdminFetchOptions = RequestInit & {
  /**
   * Override the expected response type.
   * Defaults to "json". Use "blob" for binary downloads (e.g. CSV export).
   */
  responseType?: "json" | "blob";
};

export async function adminFetch<T>(
  path: string,
  options?: AdminFetchOptions,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  const { responseType, ...init } = options ?? {};

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-admin-platform": "djayma",
      Authorization: `Bearer ${session.access_token}`,
      ...init.headers,
    },
  });

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    // Backends emit two shapes: legacy `{ error: "...", code?: "...",
    // details?: [...] }` and the envelope `{ ok: false, error: { code,
    // message, details } }`. Preserve the message, machine-readable code,
    // AND any field-level details so callers / toasts can show precise
    // validation errors.
    const rawError = (body as { error?: unknown }).error;
    const topLevelCode = (body as { code?: unknown }).code;
    const topLevelDetails = (body as { details?: unknown }).details;
    const message =
      typeof rawError === "string"
        ? rawError
        : typeof rawError === "object" && rawError !== null &&
            typeof (rawError as { message?: unknown }).message === "string"
          ? (rawError as { message: string }).message
          : `HTTP ${res.status}`;
    const code =
      typeof topLevelCode === "string"
        ? topLevelCode
        : typeof rawError === "object" && rawError !== null &&
            typeof (rawError as { code?: unknown }).code === "string"
          ? (rawError as { code: string }).code
          : undefined;
    const rawDetails =
      Array.isArray(topLevelDetails)
        ? topLevelDetails
        : typeof rawError === "object" && rawError !== null &&
            Array.isArray((rawError as { details?: unknown }).details)
          ? ((rawError as { details: unknown[] }).details)
          : undefined;
    const details = rawDetails
      ?.filter(
        (d): d is { field: unknown; message: unknown } =>
          typeof d === "object" && d !== null,
      )
      .map((d) => ({
        field: typeof d.field === "string" ? d.field : "",
        message: typeof d.message === "string" ? d.message : "",
      }))
      .filter((d) => d.field || d.message);
    throw new ApiError(res.status, message, code, details);
  }

  if (responseType === "blob") {
    return res.blob() as Promise<T>;
  }

  return res.json() as Promise<T>;
}
